import React, {Component, useEffect, useState} from "react";
import {BrowserRouter, Link, Route, Routes} from "react-router-dom";
import TeamDetailPage from "./TeamPlayersPage";
import profileStyles from '../styles/PlayerProfile.module.css'
import styles from '../styles/TeamSelectionPage.module.css'
import { useParams } from 'react-router-dom'
import {getColor} from "../helpers/colors";
import {getFlagSrc} from "../helpers/NationalityMapping";
import XGChart from "../components/XgSnake";
import SnakeChart from "../components/SnakeChart";
import SnakeChartContainer from "../components/SnakeChartContainer";

function getPositionString(nhl_position) {
    const positionMap = {
        L: 'LW',
        R: 'RW',
        C: 'C',
        D: 'D',
        G: 'G'
    };

    return positionMap[nhl_position] || '?';
}


function getAgeString(birth_date) {
    const birthDate = new Date(birth_date);
    const today = new Date();

    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    const dayDifference = today.getDate() - birthDate.getDate();

    // Adjust age if the current date is before the birth date this year
    if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
        age--;
    }

    return age;
}


function getHeightString(height_inches) {
    const feet = Math.floor(height_inches / 12);
    const inches = height_inches % 12;
    return `${feet}'${inches}"`;
}


function PlayerProfile({ player, playerCard }) {
    // console.log(playerCard)

    useEffect(() => {

        const cells = document.querySelectorAll(`.${profileStyles.analytic}`);
        cells.forEach(cell => {
            const value = parseInt(cell.getAttribute('data-value'), 10);
            const circle = cell.querySelector(`.${profileStyles.analyticCircle}`);
            if (circle) {
                circle.style.borderColor = getColor(value);
            }
        });

    const loadingContainers = document.getElementsByName('loadingBar');
        loadingContainers.forEach(loadingContainer => {
            const loadingBar = loadingContainer.querySelector(`.${profileStyles.loadingBar}`);
            const loadingText = loadingBar.querySelector(`.${profileStyles.loadingText}`);
            const loadingValue = loadingContainer.getAttribute('data-value');
            const parsedValue = loadingValue === null || loadingValue === undefined ? null : parseInt(loadingValue, 10);

            if (parsedValue === null || isNaN(parsedValue)) {
                loadingBar.style.width = `0%`;
                loadingBar.style.backgroundColor = getColor(0);
                loadingText.textContent = 'N/A';
            } else {
                const percentage = Math.max(0, Math.min(100, parsedValue));
                loadingBar.style.width = `${percentage}%`;
                loadingBar.style.backgroundColor = getColor(parsedValue);
                loadingText.textContent = `${percentage}`;
            }
        });

        const overallContainer = document.getElementById('overallBar');
        const overallBar = overallContainer.querySelector(`.${profileStyles.overallBar}`);
        const overallText = overallBar.querySelector(`.${profileStyles.overallText}`);
        const overallValueRaw = overallContainer.getAttribute('data-value');
        const overallValue = overallValueRaw === null || overallValueRaw === undefined ? null : Math.round(parseFloat(overallValueRaw));

        if (overallValue === null || isNaN(overallValue)) {
            overallBar.style.width = `0%`;
            overallBar.style.backgroundColor = getColor(0);
            overallText.textContent = 'N/A';
            overallText.style.left = '0';
            overallText.style.right = '0';
            overallText.style.textAlign = 'center';
            overallText.style.textShadow = 'none';
        } else {
            const percentage = Math.max(0, Math.min(100, overallValue));
            overallBar.style.width = `${percentage}%`;
            overallBar.style.backgroundColor = getColor(overallValue);
            overallText.textContent = `${percentage}`;
            
            // If rating is less than 10, position text to the right of the bar
            if (percentage < 10) {
                // Position text to the right of the bar by using the bar width
                const barWidth = Math.max(8, percentage); // Minimum 8px for visibility
                overallText.style.left = `${barWidth + 8}px`;
                overallText.style.right = 'auto';
                overallText.style.textAlign = 'left';
                overallText.style.color = '#000000';
                overallText.style.textShadow = 'none';
                overallText.style.zIndex = '10';
            } else {
                overallText.style.left = '8px';
                overallText.style.right = 'auto';
                overallText.style.textAlign = 'left';
                overallText.style.color = '#000000';
                overallText.style.textShadow = 'none';
                overallText.style.zIndex = '2';
            }
            // console.log(percentage)
        }
    }, [player]);
        // Reusable component for a single stat row
    const AnalyticRow = ({ label, value }) => (
      <tr>
        <td>{label}</td>
        <td className={profileStyles.analytic} data-value={value}>
          <div className={profileStyles.analyticCircle}>
            <span>{value !== null && value !== undefined ? value : 'n/a'}</span>
          </div>
        </td>
      </tr>
    );

    // Reusable component for an analytics category
    const AnalyticCategory = ({ title, loadingValue, stats }) => (
      <div className={profileStyles.analyticCategory}>
        <h1>{title}</h1>
        <div
          className={profileStyles.loadingContainer}
          data-value={loadingValue !== null && loadingValue !== undefined ? loadingValue : 'n/a'}
          name="loadingBar"
        >
          <div className={profileStyles.loadingBar}>
            <span className={profileStyles.loadingText}></span>
          </div>
        </div>
        <table>
          <tbody>
            {stats.map((stat, index) => (
              <AnalyticRow key={index} label={stat.label} value={stat.value} />
            ))}
          </tbody>
        </table>
      </div>
    );

    let statsData;
    if (player.position !== 'G') {
     statsData = {
         evenStrength: {
             title: 'Even Strength',
             loadingValue: playerCard.even_strength_rating, // Replace with dynamic value if available
             stats: [
                 {label: 'Offense', value: playerCard.ev_offense},
                 {label: 'Defense', value: playerCard.ev_defense},
             ],
         },
         specialTeams: {
             title: 'Special Teams',
             loadingValue: playerCard.special_teams_rating,
             stats: [
                 {label: 'Powerplay', value: playerCard.powerplay_rating},
                 {label: 'Penalty Kill', value: playerCard.pk_defense},
             ],
         },
         scoring: {
             title: 'Scoring',
             loadingValue: playerCard.scoring_rating,
             stats: [
                 {label: 'EV xGoals', value: playerCard.ev_expected_goals},
                 {label: 'EV Goals', value: playerCard.ev_goals},
                 {label: 'PP xGoals', value: playerCard.pp_expected_goals},
                 {label: 'PP Goals', value: playerCard.pp_goals},
                 {label: 'Shots on Goal', value: playerCard.shots_on_goal},
             ],
         },
         passing: {
             title: 'Passing',
             loadingValue: playerCard.passing_rating,
             stats: [
                 {label: 'EV Primary Assists', value: playerCard.ev_primaries},
                 {label: 'EV Secondary Assists', value: playerCard.ev_secondaries},
                 {label: 'PP Primary Assists', value: playerCard.pp_primaries},
                 {label: 'PP Secondary Assists', value: playerCard.pp_secondaries},
             ],
         },
         details: {
             title: 'Details',
             loadingValue: playerCard.details_rating,
             stats: [
                 {label: 'Penalties Taken', value: playerCard.penalties_taken},
                 {label: 'Penalties Drawn', value: playerCard.penalties_drawn},
                 {label: 'Turnovers', value: playerCard.turnovers},
                 {label: 'Takeaways', value: playerCard.takeaways},
                 {label: 'Blocked Shots', value: playerCard.blocked_shots},
                 {label: 'Hits', value: playerCard.hits},
                 {label: 'Faceoffs', value: playerCard.faceoff_percentage},
             ],
         },
         deployment: {
             title: 'Deployment',
             loadingValue: playerCard.deployment_rating,
             stats: [
                 {label: 'EV TOI', value: playerCard.ev_toi},
                 {label: 'PP TOI', value: playerCard.pp_toi},
                 {label: 'SH TOI', value: playerCard.sh_toi},
                 {label: 'O-Zone Starts', value: playerCard.ev_oz_starts},
                 {label: 'D-Zone Starts', value: playerCard.ev_dz_starts},
                 {label: 'QOT', value: playerCard.qot},
                 {label: 'QOC', value: playerCard.qoc},
            ],
        },
    }
} else {
    statsData = {
        puckStopping: {
            title: 'Puck Stopping',
            loadingValue: playerCard.puck_stopping, // Replace with dynamic value if available
            stats: [
                {label: 'Low Danger Shots', value: playerCard.low_danger_sv_percentage},
                {label: 'Medium Danger Shots', value: playerCard.medium_danger_sv_percentage},
                {label: 'High Danger Shots', value: playerCard.high_danger_sv_percentage},
                {label: 'Meaningful Shots', value: playerCard.meaningful_sv_percentage},
                {label: 'Goals Saved Above Expected', value: playerCard.gsax},
            ],
        },
        gamePerformance: {
            title: 'Game Performances',
            loadingValue: playerCard.game_performances_rating, // Replace with dynamic value if available
            stats: [
                {label: 'Quality Starts', value: playerCard.quality_starts},
                {label: 'Games Stolen', value: playerCard.games_stolen},
                {label: 'Role Difficulty', value: playerCard.xg_against},
            ],
        },
    }
}

    // Render the component
    return (
        <div className={profileStyles.page}>
            <div className={profileStyles.playerPageWrapper}>
                <div className={profileStyles.tableOutline}>
                    <div>
                        <div className={profileStyles.profileHeader}>
                            <img
                                className={profileStyles.mug}
                                src={`https://assets.nhle.com/mugs/nhl/20242025/${player.current_team}/${player.id}.png`}
                                alt={`${player.first_name} ${player.last_name}`}
                            />
                            <div className={profileStyles.teamLogoContainer}>
                                <img
                                    className={profileStyles.teamLogo}
                                    src={`https://assets.nhle.com/logos/nhl/svg/${player.current_team}_light.svg`}
                                    alt={`${player.current_team} logo`}
                                />
                            </div>
                            <div className={profileStyles.profileInfo}>
                                <div className={profileStyles.nameBarContainer}>
                                    <h1 className={profileStyles.nameBar}>
                                        {player.first_name} {player.last_name}
                                    </h1>
                                </div>
                                <div className={profileStyles.bioBackground}>
                                    <div className={profileStyles.bioContainer}>
                                        <div className={profileStyles.bioBox}>
                                            <div className={profileStyles.bioItem}>
                                                <div className={profileStyles.bioLabel}>Pos</div>
                                                <div className={profileStyles.bioValue}>{getPositionString(player.position)}</div>
                                            </div>
                                            <div className={profileStyles.bioItem}>
                                                <div className={profileStyles.bioLabel}>Age</div>
                                                <div className={profileStyles.bioValue}>{getAgeString(player.birth_date)}</div>
                                            </div>
                                            <div className={profileStyles.bioItem}>
                                                <div className={profileStyles.bioLabel}>Ht</div>
                                                <div className={profileStyles.bioValue}>{getHeightString(player.height_inches)}</div>
                                            </div>
                                            <div className={profileStyles.bioItem}>
                                                <div className={profileStyles.bioLabel}>Wt</div>
                                                <div className={profileStyles.bioValue}>{`${player.weight_lbs} lbs`}</div>
                                            </div>
                                            <div className={profileStyles.bioItem}>
                                                <div className={profileStyles.bioLabel}>Nat</div>
                                                <div className={profileStyles.bioValue}>
                                                    <img
                                                        className={profileStyles.flag}
                                                        src={getFlagSrc(player.nationality)}
                                                        alt={`${player.nationality} flag`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    className={profileStyles.overallContainer}
                                    data-value={playerCard.overall_rating_percentile}
                                    id="overallBar"
                                >
                                    <div className={profileStyles.overallBar}>
                                        <span className={profileStyles.overallText}></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr className={profileStyles.divider}
                            style={{backgroundColor: playerCard.primary_color}}/>
                        <div className={profileStyles.gridContainer}>
                            {/* Map over the statsData to render each category */}
                            {Object.values(statsData).map((category, index) => (
                                <AnalyticCategory
                                    key={index}
                                    title={category.title}
                                    loadingValue={category.loadingValue}
                                    stats={category.stats}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <SnakeChartContainer player={player} selectedSeason={'20242025'}/>
            <footer>
                <section className={profileStyles.glossary}>
                    <h2>Player Card Glossary</h2>
                    <dl>
                        {/*<dt>Note</dt>*/}
                        {/*<dd>All numbers represent per-toi percentile ranks for position among qualifying players. Percentiles are created using the most recent 150 games the player has participated in, with more weight applied to more recent games. Overall scores are not an exact science, but a mixed weighting of stats, with more emphasis placed on offensive stats for forwards and defensive stats for defensemen. Overall ratings do not always imply that one player is 'better' than another player. The nature of percentile ranks is that McDavid can only earn 99s in offensive categories, even though he might deserve a rating of above that.</dd>*/}

                        <dt>QOC</dt>
                        <dd>Quality of competition, weighted by how much time is spent head-to-head at 5v5.</dd>

                        <dt>QOT</dt>
                        <dd>Quality of teammates that the player plays with, weighted by how much time is spent together
                            at 5v5.
                        </dd>

                        <dt>QOC</dt>
                        <dd>Quality of competition, weighted by how much time is spent head-to-head at 5v5.</dd>

                        <dt>QOT</dt>
                        <dd>Quality of teammates that the player plays with, weighted by how much time is spent together
                            at 5v5.
                        </dd>

                        <dt>Quality Starts (Goalie)</dt>
                        <dd>A quality start is a game where the goalie starts and has a save percentage at or above 90%
                            when the game ends.
                        </dd>

                        <dt>Games Stolen (Goalie)</dt>
                        <dd>A game is stolen by a goalie when their team wins and the margin of victory is less than or
                            equal to the amount of goals they saved above expected.
                        </dd>

                        <dt>Meaningful Shots (Goalie)</dt>
                        <dd>A shot is considered 'meaningful' if the score is either tied or within one goal. It is
                            meant to capture the performance of the goalie in the highest stake game situations.
                        </dd>
                    </dl>
                    {/*<h2>Player Cards</h2>*/}
                </section>
            </footer>

            <p className={profileStyles.explainer}>
                All numbers represent per-toi percentile ranks for position among qualifying players.
            </p>
            <p className={profileStyles.explainer}> Percentiles are created using the most recent 210 games for
                players
                / 75 for goalies that the player has participated in, with
                more weight applied to more recent games. </p>
            <p className={profileStyles.explainer}> Overall scores are not an exact science, but a mixed
                weighting of stats, with more emphasis placed on offensive stats for forwards and defensive
                stats for defensemen. Overall ratings do not always imply that one player is 'better' than
                another player. Player cards are best used as a tool to see the specific strengths and weakness of a
                player.</p>
            <p className={profileStyles.explainer}>All data originates from NHL play-by-play data. Twitter
                @MuffinHockey</p>
        </div>
    );
}

// NEW MODERNIZED PLAYER CARD COMPONENT
function ModernPlayerProfile({ player, playerCard }) {
    useEffect(() => {
        // Add a small delay to ensure DOM elements are rendered
        const timer = setTimeout(() => {
            const cells = document.getElementsByClassName(profileStyles.modernAnalytic);
            Array.from(cells).forEach(cell => {
                const value = parseInt(cell.getAttribute('data-value'), 10);
                const circles = cell.getElementsByClassName(profileStyles.modernAnalyticCircle);
                if (circles.length > 0) {
                    const circle = circles[0];
                    circle.style.borderColor = getColor(value);
                }
            });

            const loadingContainers = document.getElementsByName('modernLoadingBar');
            Array.from(loadingContainers).forEach(loadingContainer => {
                const loadingBars = loadingContainer.getElementsByClassName(profileStyles.modernLoadingBar);
                const loadingTexts = loadingContainer.getElementsByClassName(profileStyles.modernLoadingText);
                
                if (loadingBars.length > 0 && loadingTexts.length > 0) {
                    const loadingBar = loadingBars[0];
                    const loadingText = loadingTexts[0];
                    const loadingValue = loadingContainer.getAttribute('data-value');
                    const parsedValue = loadingValue === null || loadingValue === undefined ? null : parseInt(loadingValue, 10);

                    if (parsedValue === null || isNaN(parsedValue)) {
                        loadingBar.style.width = `0%`;
                        loadingBar.style.backgroundColor = getColor(0);
                        loadingText.textContent = 'N/A';
                    } else {
                        const percentage = Math.max(0, Math.min(100, parsedValue));
                        loadingBar.style.width = `${percentage}%`;
                        loadingBar.style.backgroundColor = getColor(parsedValue);
                        loadingText.textContent = `${percentage}`;
                    }
                }
            });

            const overallContainer = document.getElementById('modernOverallBar');
            if (overallContainer) {
                const overallBars = overallContainer.getElementsByClassName(profileStyles.modernOverallBar);
                const overallTexts = overallContainer.getElementsByClassName(profileStyles.modernOverallText);
                
                if (overallBars.length > 0 && overallTexts.length > 0) {
                    const overallBar = overallBars[0];
                    const overallText = overallTexts[0];
                    const overallValueRaw = overallContainer.getAttribute('data-value');
                    const overallValue = overallValueRaw === null || overallValueRaw === undefined ? null : Math.round(parseFloat(overallValueRaw));

                    if (overallValue === null || isNaN(overallValue)) {
                        overallBar.style.width = `0%`;
                        overallBar.style.backgroundColor = getColor(0);
                        overallText.textContent = 'N/A';
                        overallText.style.left = '0';
                        overallText.style.right = '0';
                        overallText.style.textAlign = 'center';
                        overallText.style.textShadow = 'none';
                    } else {
                        const percentage = Math.max(0, Math.min(100, overallValue));
                        overallBar.style.width = `${percentage}%`;
                        overallBar.style.backgroundColor = getColor(overallValue);
                        overallText.textContent = `${percentage}`;
                        
                        // If rating is less than 10, position text to the right of the bar
                        if (percentage < 10) {
                            // Position text to the right of the bar by using the bar width
                            const barWidth = Math.max(8, percentage); // Minimum 8px for visibility
                            overallText.style.left = `${barWidth + 8}px`;
                            overallText.style.right = 'auto';
                            overallText.style.textAlign = 'left';
                            overallText.style.color = '#000000';
                            overallText.style.textShadow = 'none';
                            overallText.style.zIndex = '10';
                        } else {
                            overallText.style.left = '12px';
                            overallText.style.right = 'auto';
                            overallText.style.textAlign = 'left';
                            overallText.style.color = '#000000';
                            overallText.style.textShadow = 'none';
                            overallText.style.zIndex = '2';
                        }
                    }
                }
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [player]);

    // Reusable component for a single stat row
    const ModernAnalyticRow = ({ label, value }) => (
        <tr>
            <td className={profileStyles.modernLabel}>{label}</td>
            <td className={profileStyles.modernAnalytic} data-value={value}>
                <div className={profileStyles.modernAnalyticCircle}>
                    <span>{value !== null && value !== undefined ? value : 'n/a'}</span>
                </div>
            </td>
        </tr>
    );

    // Reusable component for an analytics category
    const ModernAnalyticCategory = ({ title, loadingValue, stats }) => (
        <div className={profileStyles.modernAnalyticCategory}>
            <h1>{title}</h1>
            <div
                className={profileStyles.modernLoadingContainer}
                data-value={loadingValue !== null && loadingValue !== undefined ? loadingValue : 'n/a'}
                name="modernLoadingBar"
            >
                <div className={profileStyles.modernLoadingBar}>
                    <span className={profileStyles.modernLoadingText}></span>
                </div>
            </div>
            <table>
                <tbody>
                    {stats.map((stat, index) => (
                        <ModernAnalyticRow key={index} label={stat.label} value={stat.value} />
                    ))}
                </tbody>
            </table>
        </div>
    );

    let statsData;
    if (player.position !== 'G') {
        statsData = {
            evenStrength: {
                title: 'Even Strength',
                loadingValue: playerCard.even_strength_rating,
                stats: [
                    {label: 'Offense', value: playerCard.ev_offense},
                    {label: 'Defense', value: playerCard.ev_defense},
                ],
            },
            specialTeams: {
                title: 'Special Teams',
                loadingValue: playerCard.special_teams_rating,
                stats: [
                    {label: 'Powerplay', value: playerCard.powerplay_rating},
                    {label: 'Penalty Kill', value: playerCard.pk_defense},
                ],
            },
            scoring: {
                title: 'Scoring',
                loadingValue: playerCard.scoring_rating,
                stats: [
                    {label: 'EV xGoals', value: playerCard.ev_expected_goals},
                    {label: 'EV Goals', value: playerCard.ev_goals},
                    {label: 'PP xGoals', value: playerCard.pp_expected_goals},
                    {label: 'PP Goals', value: playerCard.pp_goals},
                    {label: 'Shots on Goal', value: playerCard.shots_on_goal},
                ],
            },
            passing: {
                title: 'Passing',
                loadingValue: playerCard.passing_rating,
                stats: [
                    {label: 'EV Primary Assists', value: playerCard.ev_primaries},
                    {label: 'EV Secondary Assists', value: playerCard.ev_secondaries},
                    {label: 'PP Primary Assists', value: playerCard.pp_primaries},
                    {label: 'PP Secondary Assists', value: playerCard.pp_secondaries},
                ],
            },
            details: {
                title: 'Details',
                loadingValue: playerCard.details_rating,
                stats: [
                    {label: 'Penalties Taken', value: playerCard.penalties_taken},
                    {label: 'Penalties Drawn', value: playerCard.penalties_drawn},
                    {label: 'Turnovers', value: playerCard.turnovers},
                    {label: 'Takeaways', value: playerCard.takeaways},
                    {label: 'Blocked Shots', value: playerCard.blocked_shots},
                    {label: 'Hits', value: playerCard.hits},
                    {label: 'Faceoffs', value: playerCard.faceoff_percentage},
                ],
            },
            deployment: {
                title: 'Deployment',
                loadingValue: playerCard.deployment_rating,
                stats: [
                    {label: 'EV TOI', value: playerCard.ev_toi},
                    {label: 'PP TOI', value: playerCard.pp_toi},
                    {label: 'SH TOI', value: playerCard.sh_toi},
                    {label: 'O-Zone Starts', value: playerCard.ev_oz_starts},
                    {label: 'D-Zone Starts', value: playerCard.ev_dz_starts},
                    {label: 'QOT', value: playerCard.qot},
                    {label: 'QOC', value: playerCard.qoc},
                ],
            },
        }
    } else {
        statsData = {
            puckStopping: {
                title: 'Puck Stopping',
                loadingValue: playerCard.puck_stopping,
                stats: [
                    {label: 'Low Danger Shots', value: playerCard.low_danger_sv_percentage},
                    {label: 'Medium Danger Shots', value: playerCard.medium_danger_sv_percentage},
                    {label: 'High Danger Shots', value: playerCard.high_danger_sv_percentage},
                    {label: 'Meaningful Shots', value: playerCard.meaningful_sv_percentage},
                    {label: 'Goals Saved Above Expected', value: playerCard.gsax},
                ],
            },
            gamePerformance: {
                title: 'Game Performances',
                loadingValue: playerCard.game_performances_rating,
                stats: [
                    {label: 'Quality Starts', value: playerCard.quality_starts},
                    {label: 'Games Stolen', value: playerCard.games_stolen},
                    {label: 'Role Difficulty', value: playerCard.xg_against},
                ],
            },
        }
    }

    // Render the component following original structure
    return (
        <div className={profileStyles.modernPage}>
            <div className={profileStyles.modernPlayerPageContent}>
                {/* Only the card is zoomed out */}
                <div className={profileStyles.playerCardZoomWrapper}>
                  <div className={profileStyles.modernCardWrapper}>
                    <div className={profileStyles.modernPlayerPageWrapper}>
                        <div className={profileStyles.modernTableOutline}>
                            <div className={profileStyles.modernGridContainer}>
                                {/* Watermark in bottom left */}
                                <img src="/watermark.png" alt="Muffin Hockey Watermark" className={profileStyles.modernWatermark} />
                                {/* Top Row: Name and Overall Bar */}
                                <div className={profileStyles.modernHeaderRow}>
                                    <div className={profileStyles.modernNameBarContainer}>
                                        <h1 className={profileStyles.modernNameBar}>{player.first_name} {player.last_name}</h1>
                                    </div>
                                    <div className={profileStyles.modernOverallHeader}>
                                        <div className={profileStyles.modernOverallContainer} data-value={playerCard.overall_rating_percentile} id="modernOverallBar">
                                            <div className={profileStyles.modernOverallLoadingContainer}>
                                                <div className={profileStyles.modernOverallBar}></div>
                                            </div>
                                            <span className={profileStyles.modernOverallText}></span>
                                        </div>
                                    </div>
                                </div>
                                {/* Second Row: Bio Box */}
                                <div className={profileStyles.modernBioRow}>
                                    <div className={profileStyles.modernBioBackground}>
                                        <div className={profileStyles.modernBioBox}>
                                            <div className={profileStyles.modernBioItem}><div className={profileStyles.modernBioLabel}>POS</div><div className={profileStyles.modernBioValue}>{getPositionString(player.position)}</div></div>
                                            <div className={profileStyles.modernBioItem}><div className={profileStyles.modernBioLabel}>AGE</div><div className={profileStyles.modernBioValue}>{getAgeString(player.birth_date)}</div></div>
                                            <div className={profileStyles.modernBioItem}><div className={profileStyles.modernBioLabel}>HT</div><div className={profileStyles.modernBioValue}>{getHeightString(player.height_inches)}</div></div>
                                            <div className={profileStyles.modernBioItem}><div className={profileStyles.modernBioLabel}>WT</div><div className={profileStyles.modernBioValue}>{`${player.weight_lbs} lbs`}</div></div>
                                            <div className={profileStyles.modernBioItem}><img className={profileStyles.modernFlag} src={getFlagSrc(player.nationality)} alt={`${player.nationality} flag`} /></div>
                                        </div>
                                    </div>
                                </div>
                                {/* Third Row: Mug/Logo left, Stats right */}
                                <div className={profileStyles.modernTopStatsRow}>
                                    <div className={profileStyles.modernMugLogoCol}>
                                        <img className={profileStyles.modernMug} src={`https://assets.nhle.com/mugs/nhl/20242025/${player.current_team}/${player.id}.png`} alt={`${player.first_name} ${player.last_name}`} />
                                        <div className={profileStyles.modernTeamLogoContainer}>
                                            <img className={profileStyles.modernTeamLogo} src={`https://assets.nhle.com/logos/nhl/svg/${player.current_team}_light.svg`} alt={`${player.current_team} logo`} />
                                        </div>
                                    </div>
                                    <div className={profileStyles.modernTopStatsCol}>
                                        <div className={profileStyles.modernEvenStrengthSpecialTeamsRow}>
                                            <div className={profileStyles.modernEvenStrengthCol}>
                                                <ModernAnalyticCategory title={player.position !== 'G' ? "Even Strength" : "Puck Stopping"} loadingValue={player.position !== 'G' ? playerCard.even_strength_rating : playerCard.puck_stopping} stats={player.position !== 'G' ? statsData.evenStrength.stats : statsData.puckStopping.stats} />
                                            </div>
                                            <div className={profileStyles.modernSpecialTeamsCol}>
                                                <ModernAnalyticCategory title={player.position !== 'G' ? "Special Teams" : "Game Performances"} loadingValue={player.position !== 'G' ? playerCard.special_teams_rating : playerCard.game_performances_rating} stats={player.position !== 'G' ? statsData.specialTeams.stats : statsData.gamePerformance.stats} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Main Analytics Grid: Only show for non-goalies */}
                                {player.position !== 'G' && (
                                    <div className={profileStyles.modernAnalyticsGrid}>
                                        <div className={profileStyles.modernScoringCol}><ModernAnalyticCategory title="Scoring" loadingValue={playerCard.scoring_rating} stats={statsData.scoring.stats} /></div>
                                        <div className={profileStyles.modernPassingCol}><ModernAnalyticCategory title="Passing" loadingValue={playerCard.passing_rating} stats={statsData.passing.stats} /></div>
                                        <div className={profileStyles.modernDetailsCol}><ModernAnalyticCategory title="Details" loadingValue={playerCard.details_rating} stats={statsData.details.stats} /></div>
                                        <div className={profileStyles.modernDeploymentCol}><ModernAnalyticCategory title="Deployment" loadingValue={playerCard.deployment_rating} stats={statsData.deployment.stats} /></div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                  </div>
                </div>
                {/* Glossary Section */}
                <section className={profileStyles.modernGlossary}>
                    <h2>Player Card Glossary</h2>
                    <dl>
                        <dt>QOC</dt>
                        <dd>Quality of competition, weighted by how much time is spent head-to-head at 5v5.</dd>

                        <dt>QOT</dt>
                        <dd>Quality of teammates that the player plays with, weighted by how much time is spent together
                            at 5v5.
                        </dd>

                        <dt>Quality Starts (Goalie)</dt>
                        <dd>A quality start is a game where the goalie starts and has a save percentage at or above 90%
                            when the game ends.
                        </dd>

                        <dt>Games Stolen (Goalie)</dt>
                        <dd>A game is stolen by a goalie when their team wins and the margin of victory is less than or
                            equal to the amount of goals they saved above expected.
                        </dd>

                        <dt>Meaningful Shots (Goalie)</dt>
                        <dd>A shot is considered 'meaningful' if the score is either tied or within one goal. It is
                            meant to capture the performance of the goalie in the highest stake game situations.
                        </dd>
                    </dl>
                </section>
                {/* Explainer Section */}
                <div className={profileStyles.modernExplainer}>
                    <p>All numbers represent per-toi percentile ranks for position among qualifying players.</p>
                    <p>Percentiles are created using the most recent 100 games for players / 75 for goalies that the player has participated in, with more weight applied to more recent games.</p>
                    <p>Overall scores are not an exact science, but a mixed weighting of stats, with more emphasis placed on offensive stats for forwards and defensive stats for defensemen. Overall ratings do not always imply that one player is 'better' than another player. Player cards are best used as a tool to see the specific strengths and weakness of a player.</p>
                    <p>All data originates from NHL play-by-play data. Twitter @MuffinHockey</p>
                </div>
                {/* Bottom section NOT zoomed out */}
                <SnakeChartContainer player={player} selectedSeason={'20242025'}/>
            </div>
        </div>
    );
}

function PlayerPage() {
    const { playerId } = useParams();
    const [loading, setLoading] = useState(true);
    const [player, setPlayer] = useState(null);
    const [playerCard, setPlayerCard] = useState(null);

    const getData = async () => {
        try {
            let apiUrl = `/api/players/id/${playerId}/player-card`;
            const cardResp = await fetch(apiUrl);
            const cardInfo = await cardResp.json();
            setPlayerCard(cardInfo);

            apiUrl = `/api/players/id/${playerId}`;
            const playerResp = await fetch(apiUrl);
            const playerInfo = await playerResp.json();
            setPlayer(playerInfo);
        } catch (error) {
            console.error('Error fetching player data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        getData().catch((err) => {
            console.error(err);
            setLoading(false);
        });
    }, [playerId]);

    if (loading) return (
        <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    return player ? <PlayerProfile player={player} playerCard={playerCard}/> : <div>Player not found</div>;
}

// Add modernized version below the original
function PlayerPageWithModern() {
    const { playerId } = useParams();
    const [loading, setLoading] = useState(true);
    const [player, setPlayer] = useState(null);
    const [playerCard, setPlayerCard] = useState(null);

    const getData = async () => {
        try {
            let apiUrl = `/api/players/id/${playerId}/player-card`;
            const cardResp = await fetch(apiUrl);
            const cardInfo = await cardResp.json();
            setPlayerCard(cardInfo);

            apiUrl = `/api/players/id/${playerId}`;
            const playerResp = await fetch(apiUrl);
            const playerInfo = await playerResp.json();
            setPlayer(playerInfo);
        } catch (error) {
            console.error('Error fetching player data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setLoading(true);
        getData().catch((err) => {
            console.error(err);
            setLoading(false);
        });
    }, [playerId]);

    if (loading) return (
        <div className="d-flex justify-content-center">
            <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    return player ? <ModernPlayerProfile player={player} playerCard={playerCard}/> : <div>Player not found</div>;
}

export default PlayerPageWithModern


